import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { simpleCache } from '../utils/simpleCache';
import { userDataManager } from '../utils/userDataManager';

export type AppMode = 'both' | 'fuck' | 'marry';

interface UserPreferences {
  appMode: AppMode;
  cruisingEnabled: boolean;
  healthEnabled: boolean;
  activityTrackingEnabled: boolean;
  rightNowEnabled: boolean;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (newPreferences: Partial<UserPreferences>) => void;
  getBranding: () => string;
  shouldShowFeature: (feature: 'rightNow' | 'cruising' | 'fuckFilters' | 'marryFilters' | 'health' | 'activityTracking') => boolean;
  getAvailableSelectionTypes: () => ('fuck' | 'marry')[];
  lastAppModeSync: number; // Timestamp to trigger grid refresh
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const defaultPreferences: UserPreferences = {
  appMode: 'both',
  cruisingEnabled: true,
  healthEnabled: true,
  activityTrackingEnabled: true,
  rightNowEnabled: false
};

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [lastAppModeSync, setLastAppModeSync] = useState<number>(0);
  const { user } = useAuth();

  // Load preferences from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch (error) {
        console.error('Failed to parse saved preferences:', error);
      }
    }
  }, []);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  // Initial sync to Firebase when user logs in
  useEffect(() => {
    if (user?.uid && preferences.appMode) {
      console.log('🔄 Initial sync of appMode to Firebase for logged in user');
      syncToFirebase({ appMode: preferences.appMode });
    }
  }, [user?.uid]); // Only run when user logs in

  // Comprehensive cache clearing for mutual visibility updates
  const clearAllUserCaches = () => {
    console.log('🧹 Starting comprehensive cache clearing...');

    // Clear all simple cache (includes grid caches)
    const simpleCacheStats = simpleCache.getStats();
    console.log(`📊 Clearing simpleCache: ${simpleCacheStats.size} items`);
    simpleCache.clear();

    // Note: userDataManager doesn't have a clearAll method, but individual user caches
    // will naturally expire (5 min TTL) or be overwritten on next fetch

    // Clear any specific user data caches we can identify
    if (user?.uid) {
      // Clear current user's own cached data to force fresh fetch
      userDataManager.clearUser(user.uid);
      console.log(`🧹 Cleared userDataManager cache for current user: ${user.uid}`);
    }

    console.log('✅ Comprehensive cache clearing completed');
  };

  // Sync critical preferences to Firebase user profile
  const syncToFirebase = async (newPreferences: Partial<UserPreferences>) => {
    if (!user?.uid) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const firebaseUpdate: any = {};

      // Sync appMode to database for mutual visibility
      if (newPreferences.appMode !== undefined) {
        firebaseUpdate.appMode = newPreferences.appMode;
        console.log('🔄 Syncing appMode to Firebase:', newPreferences.appMode);
      }

      if (Object.keys(firebaseUpdate).length > 0) {
        console.log('🔍 Attempting Firebase write:', {
          userUid: user.uid,
          documentPath: `users/${user.uid}`,
          updateData: firebaseUpdate
        });

        await updateDoc(userRef, firebaseUpdate);
        console.log('✅ Successfully synced preferences to Firebase');

        // Verify the write by reading back immediately
        console.log('🔍 Verifying Firebase write...');
        const verifyRef = doc(db, 'users', user.uid);
        const verifySnap = await getDoc(verifyRef);
        if (verifySnap.exists()) {
          const verifyData = verifySnap.data();
          console.log('🔍 Firebase verification result:', {
            exists: true,
            appMode: verifyData.appMode || 'not set',
            fullData: verifyData
          });
        } else {
          console.log('❌ Firebase verification: Document does not exist!');
        }

        // Clear all caches when appMode changes to force mutual visibility updates
        if (newPreferences.appMode !== undefined) {
          clearAllUserCaches();

          // Add delay and timestamp update to ensure Firebase write propagates
          setTimeout(() => {
            console.log('⏰ Firebase write propagation delay completed - triggering grid refresh');
            setLastAppModeSync(Date.now()); // This will trigger grid useEffect
          }, 1500); // 1.5 second delay to allow Firebase write to propagate
        }
      }
    } catch (error) {
      console.error('❌ Failed to sync preferences to Firebase:', error);
    }
  };

  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    setPreferences(prev => {
      const updated = { ...prev, ...newPreferences };

      // If user disables fuck mode, also disable cruising
      if (newPreferences.appMode === 'marry' && updated.cruisingEnabled) {
        updated.cruisingEnabled = false;
      }

      return updated;
    });

    // Sync important changes to Firebase
    syncToFirebase(newPreferences);
  };

  const getBranding = (): string => {
    switch (preferences.appMode) {
      case 'fuck':
        return 'F#K';
      case 'marry':
        return '#MK';
      default:
        return 'FMK';
    }
  };

  const shouldShowFeature = (feature: 'rightNow' | 'cruising' | 'fuckFilters' | 'marryFilters' | 'health' | 'activityTracking'): boolean => {
    switch (feature) {
      case 'rightNow':
        return preferences.appMode !== 'marry'; // Hide if marry-only
      case 'cruising':
        return preferences.appMode !== 'marry' && preferences.cruisingEnabled; // Hide if marry-only or disabled
      case 'fuckFilters':
        return preferences.appMode !== 'marry'; // Hide if marry-only
      case 'marryFilters':
        return preferences.appMode !== 'fuck'; // Hide if fuck-only
      case 'health':
        return preferences.healthEnabled; // Show if health tracking is enabled
      case 'activityTracking':
        return preferences.activityTrackingEnabled; // Show if activity tracking is enabled
      default:
        return true;
    }
  };

  const getAvailableSelectionTypes = (): ('fuck' | 'marry')[] => {
    switch (preferences.appMode) {
      case 'fuck':
        return ['fuck'];
      case 'marry':
        return ['marry'];
      default:
        return ['fuck', 'marry'];
    }
  };

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        updatePreferences,
        getBranding,
        shouldShowFeature,
        getAvailableSelectionTypes,
        lastAppModeSync
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};